import { useEffect, useRef, useState } from "react";

import {
  faCheck,
  faChevronDown,
  faCircleNotch,
  faExpand,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import * as RadixAccordion from "@radix-ui/react-accordion";
import { m } from "framer-motion";
import { twJoin, twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";

import { TOOL_ICON_CONFIG } from "src/agents/base/ToolCard";
import { DurationPill, TokensPill } from "src/agents/MetricPills";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Divider } from "src/base-components/Divider";
import { FileCard } from "src/base-components/FileCard";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { Pill } from "src/base-components/Pill";
import { Tabs } from "src/base-components/Tabs";
import type {
  ToolCallContentBlock,
  ToolMessage,
  ToolType,
} from "src/clients/agent-management";
import { Callout } from "src/design-system/Callout";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToolCallStatus = "running" | "paused" | "success" | "error";

export type ToolCallWithResult = {
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export const formatToolOutput = (content: unknown): string => {
  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      return typeof parsed === "object"
        ? JSON.stringify(parsed, null, 2)
        : content;
    } catch {
      return content;
    }
  }
  return typeof content === "object"
    ? JSON.stringify(content, null, 2)
    : String(content);
};

export const computeToolCallStatus = (
  toolResult: ToolMessage | null,
  sessionSubstatus: string | null,
): ToolCallStatus | null => {
  if (toolResult) {
    return toolResult.status === "error" ? "error" : "success";
  }
  if (sessionSubstatus === "paused") return "paused";
  if (sessionSubstatus) return "running";
  return null;
};

// ---------------------------------------------------------------------------
// AccordionDisclosure
// ---------------------------------------------------------------------------

export const AccordionDisclosure: React.FC<{
  title: React.ReactNode;
  icon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  erroredBorder?: boolean;
  dataLoc?: string;
}> = ({
  title,
  icon,
  rightSlot,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
  erroredBorder = false,
  dataLoc,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const DISCLOSURE_ITEM_VALUE = "content";
  const rootProps = isControlled
    ? {
        value: controlledOpen ? [DISCLOSURE_ITEM_VALUE] : ([] as string[]),
        onValueChange: (values: string[]) =>
          onOpenChange?.(values.includes(DISCLOSURE_ITEM_VALUE)),
      }
    : {
        defaultValue: defaultOpen ? [DISCLOSURE_ITEM_VALUE] : ([] as string[]),
        onValueChange: (values: string[]) =>
          setInternalOpen(values.includes(DISCLOSURE_ITEM_VALUE)),
      };

  return (
    <RadixAccordion.Root type="multiple" {...rootProps}>
      <RadixAccordion.Item value={DISCLOSURE_ITEM_VALUE} asChild>
        <div
          className={twMerge(
            "rounded-lg border border-gray-200 bg-white",
            erroredBorder && "border-red-500",
          )}
          data-loc={dataLoc}
        >
          <RadixAccordion.Header asChild>
            <RadixAccordion.Trigger className="flex w-full items-center gap-2 p-3">
              <div
                className={twJoin(
                  "flex h-4 w-4 shrink-0 items-center justify-center transition-transform duration-200",
                  isOpen ? "rotate-0" : "-rotate-90",
                )}
              >
                <Icon icon={faChevronDown} size="2xs" />
              </div>
              {icon}
              <span className="min-w-0 flex-1 truncate text-left font-inter-medium-12px text-gray-800">
                {title}
              </span>
              {rightSlot}
            </RadixAccordion.Trigger>
          </RadixAccordion.Header>
          <RadixAccordion.Content asChild forceMount>
            <m.div
              animate={
                isOpen
                  ? { height: "auto", opacity: 1 }
                  : { height: 0, opacity: 0 }
              }
              initial={false}
              style={{ overflow: "hidden" }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {children}
            </m.div>
          </RadixAccordion.Content>
        </div>
      </RadixAccordion.Item>
    </RadixAccordion.Root>
  );
};

// ---------------------------------------------------------------------------
// ToolTypeIcon
// ---------------------------------------------------------------------------

export const ToolTypeIcon: React.FC<{ type: ToolType }> = ({ type }) => {
  const config = TOOL_ICON_CONFIG[type];
  if (!config) return null;
  return (
    <div
      className={twJoin(
        "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded",
        config.bgColor,
      )}
    >
      <Icon variant={config.iconVariant} icon={config.icon} size="2xs" />
    </div>
  );
};

// ---------------------------------------------------------------------------
// ToolCallStatusPill
// ---------------------------------------------------------------------------

export const ToolCallStatusPill: React.FC<{ status: ToolCallStatus }> = ({
  status,
}) => {
  if (status === "success") {
    return (
      <Pill size="sm" variant="green">
        <Pill.Icon icon={faCheck} />
        <Pill.Text>Successful</Pill.Text>
      </Pill>
    );
  }
  if (status === "error") {
    return (
      <Pill size="sm" variant="red">
        <Pill.Icon icon={faWarning} />
        <Pill.Text>Failed</Pill.Text>
      </Pill>
    );
  }
  if (status === "running") {
    return (
      <Pill size="sm" variant="orange">
        <Pill.Icon icon={faCircleNotch} spin />
        <Pill.Text>Running</Pill.Text>
      </Pill>
    );
  }
  // paused
  return (
    <Tooltip
      body="Tool is awaiting response. This may take a while."
      placement="left"
      asChild
    >
      <div>
        <Pill size="sm" variant="yellow">
          <Pill.Icon icon={faCircleNotch} spin />
          <Pill.Text>Awaiting response</Pill.Text>
        </Pill>
      </div>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// CollapsiblePre
// ---------------------------------------------------------------------------

type ToolCallContentWindowData = {
  title: string;
  content: string;
};

const COLLAPSED_MAX_HEIGHT = 200;

export const CollapsiblePre: React.FC<{
  children: string;
  title: string;
}> = ({ children, title }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  const { open: openWindow } = useFloatingWindowsActions();

  useEffect(() => {
    if (!preRef.current) return;

    const observer = new ResizeObserver(() => {
      if (!preRef.current || !preRef.current.checkVisibility()) return;
      setNeedsExpansion(preRef.current.scrollHeight > COLLAPSED_MAX_HEIGHT);
    });
    observer.observe(preRef.current);
    return () => observer.disconnect();
  }, []);

  const handleOpenFullscreen = () => {
    openWindow(
      {
        id: `tool-call-content-${uuidv4()}`,
        type: "tool-call-content",
        target: undefined,
      },
      { content: children, title } satisfies ToolCallContentWindowData,
    );
  };

  return (
    <div className="group/collapsible relative mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="absolute top-2 right-2 z-10 flex gap-1.5 opacity-0 transition-opacity group-hover/collapsible:opacity-100">
        <CopyTextIcon
          feedback="inline-only-checkmark"
          size="xs"
          tooltip="Copy"
          value={children}
        />
        <Tooltip placement="top" title="Expand" asChild>
          <IconButton
            icon={faExpand}
            size="xs"
            onClick={handleOpenFullscreen}
          />
        </Tooltip>
      </div>
      <pre
        ref={preRef}
        className={twJoin(
          "overflow-hidden whitespace-pre-wrap break-all font-mono text-gray-700 text-xs",
        )}
        style={{
          maxHeight: isExpanded ? undefined : COLLAPSED_MAX_HEIGHT,
        }}
      >
        {children}
      </pre>
      {needsExpansion && (
        <button
          className="mt-1 font-inter-medium-12px text-indigo-500 hover:text-indigo-600"
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "See less" : "See more"}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ToolCallBody
// ---------------------------------------------------------------------------

export const ToolCallBody: React.FC<{
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
}> = ({ toolCall, toolResult }) => {
  const isError = toolResult?.status === "error";

  const isReadFile =
    toolCall.metadata.type === "read_file" &&
    toolResult?.artifact?.file_id &&
    toolResult?.artifact?.file_env;

  const tabs = [
    {
      key: "input",
      label: "Input",
      content: (
        <CollapsiblePre title={`"${toolCall.metadata.name}" tool input`}>
          {JSON.stringify(toolCall.args, null, 2)}
        </CollapsiblePre>
      ),
    },
    {
      key: "output",
      label: "Output",
      disabled: !toolResult,
      content: toolResult ? (
        isReadFile ? (
          <div className="mt-3">
            <FileCard
              fileId={toolResult.artifact!.file_id}
              fileEnv={toolResult.artifact!.file_env}
              floatingWindowPlacement="left"
              floatingWindowOffset={[0, 600]}
            />
          </div>
        ) : (
          <CollapsiblePre title={`"${toolCall.metadata.name}" tool output`}>
            {formatToolOutput(toolResult.content)}
          </CollapsiblePre>
        )
      ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-3 px-3 pb-3">
      <Divider />
      {toolCall.metadata.description && (
        <div>
          <span className="font-inter-medium-11px text-gray-500">
            Description
          </span>
          <p className="line-clamp-3 font-inter-normal-13px text-gray-800">
            {toolCall.metadata.description}
          </p>
        </div>
      )}
      {isError && (
        <Callout type="error">
          <span className="font-inter-normal-12px">
            {formatToolOutput(toolResult.content)}
          </span>
        </Callout>
      )}
      <Tabs
        defaultActiveKey="input"
        tabClassName="px-2"
        tabListClassName="border-b border-gray-200"
        tabs={tabs}
        unmountPanels={false}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// ToolCallAccordionItem
// ---------------------------------------------------------------------------

export const ToolCallAccordionItem: React.FC<{
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
  status: ToolCallStatus | null;
}> = ({ toolCall, toolResult, status }) => {
  const durationMs = toolResult
    ? new Date(toolResult.finished_at).getTime() -
      new Date(toolResult.started_at).getTime()
    : null;

  return (
    <AccordionDisclosure
      erroredBorder={status === "error"}
      icon={<ToolTypeIcon type={toolCall.metadata.type} />}
      rightSlot={
        <div className="flex items-center gap-1.5">
          {toolResult && durationMs != null && (
            <Tooltip placement="top" title="Execution duration">
              <DurationPill durationMs={durationMs} />
            </Tooltip>
          )}
          {toolResult && (
            <Tooltip placement="top" title="Token usage">
              <TokensPill count={toolResult.token_count} />
            </Tooltip>
          )}
          {status && <ToolCallStatusPill status={status} />}
        </div>
      }
      title={toolCall.metadata.name}
    >
      <ToolCallBody toolCall={toolCall} toolResult={toolResult} />
    </AccordionDisclosure>
  );
};
